import React from "react";
import { Box, Typography } from "@mui/material";
import { isMobile, isTablet } from "react-device-detect";
import { useFeatureFlags } from "../context/FeatureFlagContext";

/**
 * MobileBlocker Component
 *
 * Blocks access to the application on mobile phones while allowing tablets.
 * Controlled by the mobileBlockerEnabled feature flag.
 *
 * Detection strategy:
 * - Uses react-device-detect for device type (phone vs tablet)
 * - Uses viewport width as fallback (< 768px = blocked)
 * - Tablets (iPad) are allowed (768px+)
 */
const MobileBlocker = ({ children }: { children: React.ReactNode }) => {
  const { mobileBlockerEnabled } = useFeatureFlags();

  // Allow tablets (iPad), block phones
  const isPhoneDevice = isMobile && !isTablet;

  // Also check viewport width as fallback
  const [isSmallScreen, setIsSmallScreen] = React.useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );

  React.useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const shouldBlock = mobileBlockerEnabled && (isPhoneDevice || isSmallScreen);

  if (shouldBlock) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "#1F2937",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
          textAlign: "center",
          zIndex: 9999,
        }}
      >
        {/* Logo */}
        <Box
          component="img"
          src="https://pretestlogoez.s3.amazonaws.com/email/logo-white.png"
          alt="EZ Cloud"
          sx={{
            width: "180px",
            height: "auto",
            marginBottom: "40px",
          }}
        />

        {/* Heading */}
        <Typography
          variant="h5"
          sx={{
            color: "var(--bg-surface)",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 700,
            marginBottom: "16px",
          }}
        >
          Desktop or Tablet Required
        </Typography>

        {/* Message */}
        <Typography
          sx={{
            color: "#9CA3AF",
            fontFamily: "Montserrat, sans-serif",
            fontSize: "16px",
            lineHeight: 1.6,
            maxWidth: "320px",
            marginBottom: "32px",
          }}
        >
          EZ Cloud is optimized for desktop computers and tablets. Please access this application
          from a larger screen for the best experience.
        </Typography>

        {/* Supported devices */}
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderRadius: "12px",
            padding: "20px 24px",
            maxWidth: "280px",
          }}
        >
          <Typography
            sx={{
              color: "#FF7519",
              fontFamily: "Montserrat, sans-serif",
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "1px",
              marginBottom: "12px",
            }}
          >
            Supported Devices
          </Typography>
          <Typography
            sx={{
              color: "#D1D5DB",
              fontFamily: "Montserrat, sans-serif",
              fontSize: "14px",
              lineHeight: 1.8,
            }}
          >
            Desktop computers
            <br />
            Laptops
            <br />
            iPad and tablets
          </Typography>
        </Box>

        {/* Support link */}
        <Typography
          sx={{
            color: "#6B7280",
            fontFamily: "Montserrat, sans-serif",
            fontSize: "13px",
            marginTop: "40px",
          }}
        >
          Need help?{" "}
          <Box
            component="a"
            href="mailto:support@ezcloud.ai"
            sx={{
              color: "#FF7519",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Contact Support
          </Box>
        </Typography>
      </Box>
    );
  }

  return children;
};

export default MobileBlocker;
